<template>
    <div>
        <Modal title="Create New Cohort" width="push-40" section="showNewCohortDialog" @close="close">
            <div class="card createActivation">
                <div class="card-body">
                    <v-form ref="form">
                        <div class="row" v-if="base_identifier === 'ip' && cohort_type === 'file'">
                            <div class="col-12">
                                <div class="alert alert-info" role="alert">
                                    <strong><i class="fas fa-info-circle pr-2"></i></strong>
                                    Your imported IP file will be encrypted before it begins its analysis.
                                </div>
                            </div>
                        </div>

                        <div class="row">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Cohort Source</span>
                                </v-tooltip>
                                <i class="fas fa-object-group"></i> <span style="padding-left: 5px;"> Source</span>
                            </div>
                            <div class="form-group col-md-6">
                                <v-select
                                    label="Select Cohort Source"
                                    v-model="cohort_type"
                                    :items="cohort_types"
                                ></v-select>
                            </div>
                        </div>

                        <div class="row">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Cohort Name</span>
                                </v-tooltip>
                                <i class="fas fa-cog" ></i> <span style="padding-left: 5px;"> Cohort Name</span>
                            </div>
                            <div class="form-group col-md-6">
                                <v-text-field
                                    v-model="name"
                                    label="Cohort Name"
                                    :rules="[requiredName, nameMinimumChars]"
                                ></v-text-field>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="! ['audience'].includes(cohort_type)">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Base Identifier for the Cohort Source</span>
                                </v-tooltip>
                                <i class="fas fa-mobile-alt"></i> <span style="padding-left: 5px;"> Base Identifier</span>
                            </div>
                            <div class="form-group col-md-6">
                                <v-select
                                    label="Select Base Identifier"
                                    v-model="base_identifier"
                                    :items="filteredBaseIdentifiers"
                                ></v-select>
                            </div>
                        </div>

                        <div class="row" v-if="['file'].includes(cohort_type)">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Upload File Source</span>
                                </v-tooltip>
                                <i class="fas fa-folder-open"></i> <span style="padding-left: 5px;"> File Source</span>
                            </div>
                            <div class="form-group col-md-6">
                                <v-select
                                    label="Select File Source"
                                    v-model="file_source"
                                    :items="file_sources"
                                ></v-select>
                            </div>
                        </div>

                        <div class="row" v-if="['file'].includes(cohort_type) && ['upload'].includes(file_source)">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Cohort File (.csv or .gz only)</span>
                                </v-tooltip>
                                <i class="fas fa-file-excel" ></i> <span style="padding-left: 5px;"> Cohort File</span>
                            </div>
                            <div class="form-group col-md-6">
                                <v-file-input
                                    show-size
                                    accept=".csv, .gz"
                                    v-model="file"
                                    ref="input_file"
                                    truncate-length="15"
                                    label="Cohort File"
                                    :error-messages="errorMessage"
                                ></v-file-input>

                                <v-progress-linear
                                    v-if="uploading"
                                    :color="color"
                                    buffer-value="0"
                                    height="5"
                                    stream
                                    rounded
                                    v-model="progress"
                                >
                                </v-progress-linear>

                                <v-progress-linear
                                    class="mt-2"
                                    v-if="encrypting"
                                    color="cyan"
                                    height="5"
                                    indeterminate
                                    rounded
                                ></v-progress-linear>
                                <span v-if="encrypting" class="text-info">Encrypting, Hashing and Salting...</span>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="['file'].includes(cohort_type) && ['s3'].includes(file_source)">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>AWS S3 URI</span>
                                </v-tooltip>
                                <i class="fab fa-aws" ></i> <span style="padding-left: 5px;"> S3 URI</span>
                            </div>
                            <div class="form-group col-md-6">
                                <v-text-field
                                    v-model="s3_uri"
                                    label="AWS S3 URI"
                                    placeholder="s3://bucket/path/file.csv"
                                    :rules="[requiredStringValue, csvRequired, withoutS3PrefixRequired, mustBeInSubfolder]"
                                ></v-text-field>

                                <v-checkbox
                                    v-model="permissions_confirmation"
                                    :rules="[requiredToggle]"
                                    class="align-checkbox-top"
                                >
                                    <template v-slot:label>
                                        <div class="checkbox-content">
                                            I confirm that the client or data provider has granted Intuizi explicit permission to access the specified S3 location, in accordance with the 
                                            <a 
                                                href="https://docs.intuizi.com/knowledge-base/misc/data-access-and-integration-guide/" 
                                                target="_blank"
                                                @click.stop
                                            >
                                                Data Access and Integration Guide
                                            </a>.
                                        </div>
                                    </template>
                                </v-checkbox>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="cohort_type === 'audience'">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Select from Completed Audience</span>
                                </v-tooltip>
                                <i class="fas fa-database"></i> <span style="padding-left: 5px;"> Audience</span>
                            </div>
                            <div class="form-group col-md-6 mb-0">
                                <v-autocomplete
                                    v-model="audience_selected"
                                    :items="audiences"
                                    label="My Audiences"
                                    :rules="[requiredFieldSelect]"
                                ></v-autocomplete>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="['pixel'].includes(cohort_type)">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Select from Active Advertising Campaign</span>
                                </v-tooltip>
                                <i class="fas fa-database"></i> <span style="padding-left: 5px;"> Advertising</span>
                            </div>
                            <div class="form-group col-md-6 mb-0">
                                <v-autocomplete
                                    v-model="pixel_selected"
                                    :items="pixels"
                                    label="Select Advertising Campaign"
                                    :rules="[requiredFieldSelect]"
                                ></v-autocomplete>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="['pixel'].includes(cohort_type)">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Choose a date period for the campaign data.</span>
                                </v-tooltip>

                                <i class="far fa-calendar-alt"></i> <span style="padding-left: 5px;"> Recency</span>
                            </div>
                            <div class="form-group col-md-6">
                                <div class="input-daterange form-inline datesSelection" >
                                    <v-menu
                                        v-model="start_date_menu"
                                        :close-on-content-click="false"
                                        :nudge-right="40"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="auto"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field
                                                v-model="start_date"
                                                label="Start Date"
                                                clearable
                                                readonly
                                                v-bind="attrs"
                                                v-on="on"
                                                :rules="[requiredStartDate]"
                                            ></v-text-field>
                                        </template>
                                        <v-date-picker
                                            header-color="#fb7e42"
                                            color="#fb7e42"
                                            v-model="start_date"
                                            @input="start_date_menu = false"
                                        ></v-date-picker>
                                    </v-menu>
                                    <span class="mx-2">to</span>
                                    <v-menu
                                        v-model="end_date_menu"
                                        :close-on-content-click="false"
                                        :nudge-right="40"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="auto"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field
                                                v-model="end_date"
                                                label="End Date"
                                                clearable
                                                readonly
                                                v-bind="attrs"
                                                v-on="on"
                                                :rules="[requiredEndDate]"
                                            ></v-text-field>
                                        </template>
                                        <v-date-picker
                                            header-color="#fb7e42"
                                            color="#fb7e42"
                                            v-model="end_date"
                                            @input="end_date_menu = false"
                                        ></v-date-picker>
                                    </v-menu>
                                </div>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="['pixel'].includes(cohort_type)">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Select Campaign Parameters (Keys & Values)</span>
                                </v-tooltip>
                                <i class="fas fa-filter"></i> <span style="padding-left: 5px;"> Parameters</span>
                            </div>
                            <div class="form-group col-md-3 mb-0">
                                <v-autocomplete
                                    v-model="pixels_key"
                                    :items="$store.getters['CreateCohortStore/getPixelsKeys']"
                                    label="Key"
                                ></v-autocomplete>
                            </div><!--form-group-->
                            <div class="form-group col-md-3 mb-0">
                                <v-text-field
                                    v-model="pixels_value"
                                    label="Value"
                                ></v-text-field>
                            </div>
                            <div class="form-group col-md-1 mb-0">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                            @click="addNewInputField()"
                                        >
                                            <v-icon color="success lighten-1" class="tooltip-small">
                                                mdi-plus-circle
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Add additional Key & Value pair</span>
                                </v-tooltip>
                            </div>
                        </div>

                        <InputField v-if="all_pixels_keys.length > 1 && index > 0" v-for="(item, index) in all_pixels_keys" :key="index" :indexItem="index"></InputField>

                        <div class="row" v-if="! ['audience'].includes(cohort_type)">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Enter the limit of devices to be used. <br> Leave blank to keep all.</span>
                                </v-tooltip>
                                <i class="fas fa-tachometer-alt" ></i> <span style="padding-left: 5px;"> Limit</span>
                            </div>
                            <div class="form-group col-md-3">
                                <v-text-field
                                    v-model="results_count"
                                    label='Limit Audience'
                                    type="number"
                                    min="0"
                                ></v-text-field>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="is_audience_data_available">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Enter the limit of devices to be exported.</span>
                                </v-tooltip>
                                <i class="fas fa-tachometer-alt" ></i> <span style="padding-left: 5px;"> Limit</span>
                            </div>
                            <div class="form-group col-md-3">
                                <v-text-field
                                    v-model="results_count"
                                    label='Limit Audience'
                                    type="number"
                                    min="0"
                                    :max="single_audience_data.resultsCount"
                                    :rules="[requiredAudience]"
                                ></v-text-field>
                                <small>Maximum {{ new Intl.NumberFormat().format(single_audience_data.resultsCount) }} devices.</small>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="is_limit_by_distance_data_available">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Select this option if you want to limit <br> the cohort devices based on Distance Analysis.</span>
                                </v-tooltip>
                                <i class="fas fa-ruler-horizontal" ></i> <span style="padding-left: 5px;"> Limit by Distance</span>
                            </div>
                            <div class="form-group col-md-4">
                                <v-tooltip
                                    v-if="limit_by_freq"
                                    top
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="blue lighten-1">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Only one type of limit can be applied.</span>
                                </v-tooltip>

                                <v-switch
                                    class="pt-5"
                                    v-model="limit_by_distance"
                                    inset
                                    :disabled="limit_by_freq"
                                ></v-switch>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="limit_by_distance">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>To limit the cohort devices by distance <br> adjust the max distance in meters.</span>
                                </v-tooltip>
                                <i class="fas fa-ruler-horizontal"></i> <span style="padding-left: 5px;"> Distance (meters)</span>
                            </div>
                            <div class="form-group col-md-4">
                                <template>
                                    <v-slider
                                        v-model="distance_limit"
                                        :max="distance_max"
                                        :min="distance_min"
                                        step="25"
                                        hide-details
                                        thumb-label="always"
                                    >
                                    </v-slider>
                                </template>
                            </div><!--form-group-->
                        </div>


                        <div class="row" v-if="is_limit_by_daily_frequency_data_available">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Select this option if you want to limit <br> the cohort devices based on Frequency Analysis.</span>
                                </v-tooltip>
                                <i class="fas fa-tachometer-alt" ></i> <span style="padding-left: 5px;"> Limit by Frequency</span>
                            </div>
                            <div class="form-group col-md-4">
                                <v-tooltip
                                    v-if="limit_by_distance"
                                    top
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="blue lighten-1">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Only one type of limit can be applied.</span>
                                </v-tooltip>

                                <v-switch
                                    class="pt-5"
                                    v-model="limit_by_freq"
                                    inset
                                    :disabled="limit_by_distance"
                                ></v-switch>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="limit_by_freq && !single_audience_data.analyses.frequency.day_part">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>To limit the cohort devices by frequency <br> adjust the frequency min/max range.</span>
                                </v-tooltip>
                                <i class="fa fa-street-view"></i> <span style="padding-left: 5px;"> Freq. Range</span>
                            </div>
                            <div class="form-group col-md-6">
                                <v-range-slider
                                    v-model="freqRange"
                                    :max="max"
                                    :min="min"
                                    hide-details
                                    class="align-center"
                                >
                                    <template v-slot:prepend>
                                        <span class="pt-3 mr-2">Min:</span>
                                        <v-text-field
                                            :value="freqRange[0]"
                                            class="mt-0 pt-0 custom-input"
                                            hide-details
                                            single-line
                                            type="number"
                                            :min="min"
                                            :max="max"
                                            style="width: 60px"
                                            @change="$set(freqRange, 0, $event)"
                                        ></v-text-field>
                                    </template>
                                    <template v-slot:append>
                                        <span class="pt-3 mr-2">Max:</span>
                                        <v-text-field
                                            :value="freqRange[1]"
                                            class="mt-0 pt-0 custom-input"
                                            hide-details
                                            single-line
                                            type="number"
                                            :min="min"
                                            :max="max"
                                            style="width: 60px"
                                            @change="$set(freqRange, 1, $event)"
                                        ></v-text-field>
                                    </template>
                                </v-range-slider>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="limit_by_freq && single_audience_data.analyses.frequency.day_part">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>To limit the cohort devices by frequency day part, select the beginning and ending hours. <br> Hours are adjusted according to the locations' time zone.</span>
                                </v-tooltip>
                                <i class="fa fa-street-view"></i> <span style="padding-left: 5px;"> Freq. Range</span>
                            </div>
                            <div class="form-group col-md-2">
                                <v-select
                                    label="Start Hour"
                                    v-model="freqRange[0]"
                                    :items="this.day_hours"
                                    @change="$set(freqRange, 0, $event)"
                                ></v-select>
                            </div><!--form-group-->
                            <div class="form-group col-md-2">
                                <v-select
                                    label="End Hour"
                                    v-model="freqRange[1]"
                                    :items="this.day_hours"
                                    @change="$set(freqRange, 1, $event)"
                                ></v-select>
                            </div><!--form-group-->
                        </div>

                        <div class="row" v-if="limit_by_freq || limit_by_distance">
                            <div class="form-inline col-md-2">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-icon color="grey lighten-1" class="tooltip-small">
                                                mdi-information-outline
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Devices Limit</span>
                                </v-tooltip>

                                <i class="fas fa-tachometer-alt"></i>
                                <span v-if="single_audience_data.analyses.frequency.day_part" style="padding-left: 5px;"> Hourly Unique Visits</span>
                                <span v-else style="padding-left: 5px;"> Limit Audience</span>
                            </div>
                            <div class="form-group col-md-4">
                                <h5 class="pt-2">{{ new Intl.NumberFormat().format(this.total) }} </h5>
                            </div><!--form-group-->
                        </div>

                        <hr>

                        <div class="row">
                            <div class="col-1 text-left">
                                <button @click.prevent="close()" class="btn btn-danger">Cancel</button>
                            </div>
                            <div class="col-2 offset-9 text-right">
                                <button @click.prevent="submitData" :disabled="submissionDisabled" class="btn btn-success">Create</button>
                            </div>
                        </div>
                    </v-form>
                </div>
            </div>
        </Modal>
    </div>
</template>

<script>
import Modal from '../Modal'
import api from "../../api/analyses/activations";
import sch_api from "../../api/analyses/scheduler";
import mixins from "../../mixin";
import { EventBus } from "../../events";
import InputField from "./partials/cohorts/InputField.vue";

export default {
    name: "CreateNewCohortByFile",
    mixins: [mixins],
    components: {
        InputField,
        Modal
    },
    props: [
        'audience'
    ],
    data: () => ({
        cohort_type: 'file',
        cohort_types: [
            {
                value: 'file',
                text: 'Import File'
            },
            {
                value: 'audience',
                text: 'My Audience'
            },
            {
                value: 'pixel',
                text: 'Advertising'
            }
        ],

        base_identifier: 'eid',
        base_identifiers: [
            {
                value: 'eid',
                text: 'EIDs'
            },
            {
                value: 'ip',
                text: 'IPs'
            },
            {
                value: 'hem',
                text: 'HEMs (SHA-256)'
            }
        ],

        file_source: 'upload',
        file_sources: [
            {
                value: 'upload',
                text: 'Upload File'
            },
            {
                value: 's3',
                text: 'S3 URI'
            }
        ],

        name: '',
        s3_uri: '',
        permissions_confirmation: false,
        file: [],
        serverFileName: null,
        originalFileName: null,
        encrypting: false,

        audiences: [],
        audience_selected: null,
        single_audience_data: null,

        pixels: [],
        pixel_selected: null,
        start_date: null,
        end_date: null,

        start_date_menu: false,
        end_date_menu: false,

        limit_by_freq: false,
        limit_by_distance: false,

        results_count: null,

        min: null,
        max: null,
        freqRange: [null, null],
        is_day_part: false,

        distance_min: null,
        distance_max: null,
        distance_limit: null,
        total: 0,

        progress: 0,
        uploading: false,
        color: 'cyan',
        errorMessage: '',

        submissionDisabled: true,

        requiredName: v => v && v.length > 0 || 'Cohort Name field is required',
        requiredStringValue: v => v && v.length > 0 || 'This field is required',
        nameMinimumChars: v => v && v.length > 4 || 'Cohort Name must contain minimum 5 characters',
        requiredFieldSelect: v => v && v > 0 || 'This field is required',
        requiredToggle: v => v && v || 'This field is required',
        requiredAudience: v => v && v > 0 || 'Limit audience must be greater then 0',
        requiredStartDate: v => v && v.length > 0 || 'Start Date field is required',
        requiredEndDate: v => v && v.length > 0 || 'End Date field is required',
        csvRequired: v =>
            v && v.length > 0 &&
            (v.endsWith('.csv') || v.endsWith('.csv.gz')) ||
            'Only .csv and .csv.gz files are allowed',
        withoutS3PrefixRequired: v => {
            if (!v || v.length === 0) return 'This field is required'
            if (! v.startsWith('s3://')) return 'S3 URI must be with the s3:// prefix'
            if (!v.includes('/')) return 'S3 URI must include bucket and path (e.g. bucket-name/path/file.csv)'
            return true
        },
      mustBeInSubfolder: v => {
        if (!v || v.length === 0) return true // Let other rules handle required
        const segments = v.split('/')
        if (segments.length < 2) {
          return 'S3 URI must include a subfolder (e.g. bucket-name/path/file.csv)'
        }
        return true
      }

    }),
    methods: {
        submitData() {
            if (this.$refs.form.validate()) {
                this.createNewCohort()
            }
        },
        addNewInputField() {
            this.$store.dispatch('CreateCohortStore/addNewInputField')
        },
        decryptionCompleted: _.debounce(function() {
            this.encrypting = false;
            this.submissionDisabled = false
        }, Math.floor(Math.random() * (8000 - 10000 + 1)) + 8000),
        async fetchAudiences() {
            await sch_api.getAllAudiences()
                .then(res => {
                    this.audiences = res.data
                })
                .catch(e => {
                    this.$toast.open({
                        message: e.response.data.message,
                        type: "error",
                    })
                    this.close()
                })
        },
        async fetchPixels() {
            await api.getPixels()
                .then(res => {
                    this.pixels = res.data
                })
                .catch(e => {
                    this.$toast.open({
                        message: e.response.data.message,
                        type: "error",
                    })
                    this.close()
                })
        },
        async fetchPixelKeys() {
            await api.getPixelKeys()
                .then(res => {
                    this.pixels_keys = res.data
                })
                .catch(e => {
                    this.$toast.open({
                        message: e.response.data.message,
                        type: "error",
                    })
                    this.close()
                })
        },
        async fetchAudience() {
            await api.getSingleAudience({
                audience: this.audience_selected
            })
            .then(res => {
                this.limit_by_freq = false
                this.limit_by_distance = false
                this.name = res.data.activation.name
                this.single_audience_data = res.data.activation
                this.results_count = parseInt(res.data.activation.resultsCount)
                this.is_day_part = res.data.activation.analyses.frequency.day_part

                if (res.data.activation.freqResults.length) {
                    this.setRangeMinAndMax(res.data.activation.freqResults, 'frequency')
                    this.calculateTotalVisits()
                }

                if (res.data.activation.distanceResults.length) {
                    this.setRangeMinAndMax(res.data.activation.distanceResults, 'distance')
                    this.calculateTotalVisitsByDistance()
                }
            })
        },
        async createNewCohort() {
            await api.createCohortFromFile({
                source: this.cohort_type,
                name: this.name,
                audience: this.audience_selected,

                base_identifier: this.base_identifier,
                file_source: this.file_source,

                pixel: this.pixel_selected,
                pixel_keys: this.$store.getters['CreateCohortStore/getAllPixelKey'],
                pixel_values: this.$store.getters['CreateCohortStore/getAllPixelValue'],
                start_date: this.start_date,
                end_date: this.end_date,

                limit: this.results_count,
                total_devices: this.single_audience_data ? this.single_audience_data.resultsCount : null,

                freq_limit: this.limit_by_freq,
                distance_limit: this.limit_by_distance,

                freq_min: this.freqRange[0],
                freq_max: this.freqRange[1],
                is_day_part: this.is_day_part,

                distance: this.distance_limit,

                cohort_file: this.getCohortFilePath()
            })
            .then(res => {
                this.submissionDisabled = true
                this.$toast.open({
                    message: res.data.message,
                    type: "success",
                })
                this.serverFileName = ''
                EventBus.$emit('newCohortCreated')

                this.redirectToCohortPage()
            })
            .catch(e => {
                this.$toast.open({
                    message: e.response.data.message,
                    type: "error",
                })
                this.close()
            })
        },
        redirectToCohortPage: _.debounce(function() {
            window.location.href = this.cohorts_url
        }, 800),
        async uploadCohortFile(formData) {
            this.submissionDisabled = true
            await api.uploadCohortFile(
                formData,
                {
                    headers: {'Content-Type': 'multipart/form-data'},
                    onUploadProgress: function( progressEvent ) {
                        this.progress = parseInt( Math.round( ( progressEvent.loaded / progressEvent.total ) * 100 ) );
                    }.bind(this)
                })
                .then(res => {
                    this.serverFileName = res.data.filename
                    if (this.base_identifier === 'ip') {
                        this.encrypting = true

                        this.decryptionCompleted()
                    } else {
                        this.submissionDisabled = false
                    }
                })
                .catch(e => {
                    if (e.response.data.errors) {
                        this.fileErrors = e.response.data.errors
                        this.originalFileName = e.response.data.originalName
                    }
                    this.$toast.open({
                        message: e.response.data.message,
                        type: "error",
                    })
                    this.file = null
                })
        },
        prepareForm() {
            let formData = new FormData();
            formData.append('cohort_file', this.file);

            this.uploadCohortFile(formData)
        },
        getCohortFilePath() {
            if (this.file_source === 'upload') {
                return this.serverFileName
            } else {
                return this.s3_uri.slice(5)
            }
        },
        async removeCohortFile() {
            await api.removeCohortFile({
                filename: this.serverFileName
            })
        },
        close() {
            if (this.serverFileName) {
                this.removeCohortFile()
            }
            this.$store.commit('CreateCohortStore/RESET_STATE')

            this.$parent.showNewCohortDialog = false
            this.$parent.cohortAudience = []
        },
        setRangeMinAndMax(results, type) {
            const min = parseInt(results[0].index)
            const max = parseInt(results[results.length - 1].index)

            if (type === 'frequency') {
                this.min = min
                this.max = max
                this.freqRange[0] = min
                this.freqRange[1] = max

            } else if (type === 'distance') {
                this.distance_min = min
                this.distance_max = max
                this.distance_limit = max
                this.total = results[results.length - 1]['counts']

            }
        },
        calculateTotalVisits() {
            this.total = 0

            this.single_audience_data.freqResults.forEach((item) => {
                if (item.index >= this.freqRange[0] && item.index <= this.freqRange[1]) {
                    this.total = this.total + item.counts
                }
            })
        },
        calculateTotalVisitsByDistance() {
            this.total = 0

            this.single_audience_data.distanceResults.forEach((item) => {
                if (parseInt(item.index) === parseInt(this.distance_limit)) {
                    this.total = item.counts
                }
            })
        },
    },
    computed: {
        is_audience_data_available: function () {
            return this.cohort_type === 'audience' && this.single_audience_data && !this.limit_by_freq && !this.limit_by_distance
        },
        is_limit_by_distance_data_available: function () {
            return this.cohort_type === 'audience' && this.single_audience_data && ((this.single_audience_data.analyses && this.single_audience_data.analyses.distance && this.single_audience_data.analyses.distance.process) && this.single_audience_data.distanceResults.length)
        },
        is_limit_by_daily_frequency_data_available: function () {
            return this.cohort_type === 'audience' && this.single_audience_data && ((this.single_audience_data.analyses && this.single_audience_data.analyses.frequency.process) && this.single_audience_data.freqResults.length)
        },
        filteredBaseIdentifiers: function() {
          if (this.cohort_type === 'pixel') {
            return this.base_identifiers.filter(identifier =>
                identifier.value === 'eid' || identifier.value === 'ip'
            );
          }
          return this.base_identifiers;
        },
        all_pixels_keys() {
            return this.$store.getters['CreateCohortStore/getAllPixelKey']
        },
        pixels_key: {
            get() {
                return this.$store.getters['CreateCohortStore/getPixelKey'](0)
            },
            set(value) {
                this.$store.commit('CreateCohortStore/SET_PIXELS_KEY', {
                    index: 0,
                    value: value
                })
            }
        },
        pixels_value: {
            get() {
                return this.$store.getters['CreateCohortStore/getPixelValue'](0)
            },
            set(value) {
                this.$store.commit('CreateCohortStore/SET_PIXELS_VALUE', {
                    index: 0,
                    value: value
                })
            }
        },
    },
    mounted() {
        if (this.audience.length !== 0) {
            this.fetchAudiences()
            this.fetchPixels()
            this.cohort_type = 'audience'
            this.audience_selected = this.audience.id
        }
    },
    watch: {
        file: function () {
            if (this.serverFileName) {
                this.removeCohortFile()
            }

            if (this.file) {
                this.fileErrors = [];
                if (this.file.size < (this.allowedFileSize * 1000000)) {
                    this.errorMessage = ''
                    this.uploading = true
                    this.progress = 1
                    this.prepareForm()
                } else {
                    this.errorMessage = 'Maximum allowed filesize is ' + this.allowedFileSize + ' MB'
                }
            } else {
                this.errorMessage = ''
                this.serverFileName = null
                this.uploading = false
                this.progress = 0
                this.submissionDisabled = true
            }
        },
        progress: function () {
            if (this.progress === 100) {
                this.color = 'success'
            }
        },
        cohort_type: function () {
            if (this.cohort_type === 'audience') {
                this.fetchAudiences()
            }

            if (['pixel'].includes(this.cohort_type)) {
                this.fetchPixels()
                this.$store.dispatch('CreateCohortStore/loadPixelKeys')

                // Reset identifier to eid
                if (this.base_identifier === 'hem') {
                  this.base_identifier = 'eid'
                }
            }
        },
        audience_selected: function () {
            this.fetchAudience()
            this.submissionDisabled = false
        },
        pixel_selected: function () {
            this.submissionDisabled = false
        },
        limit_by_freq: function () {
            if (this.limit_by_freq) {
                this.limit_by_distance = false
            }
        },
        limit_by_distance: function () {
            if (this.limit_by_distance) {
                this.limit_by_freq = false
            }
        },
        file_source: function () {
            if (this.file_source === 's3') {
                this.submissionDisabled = false
            }
        },
        distance_limit: function () {
            this.calculateTotalVisitsByDistance()
        },
        freqRange: function() {
            this.calculateTotalVisits()
        }
    }
}
</script>

<style scoped>
.align-checkbox-top :deep(.v-input--selection-controls) {
    margin-top: 0;
    padding-top: 0;
}

.align-checkbox-top :deep(.v-input--selection-controls__input) {
    margin-top: 2px !important;
}

.align-checkbox-top :deep(.v-label) {
    align-self: flex-start;
    margin-top: 0;
}

.checkbox-content {
    display: inline-block;
    margin-top: -4px;
}

.checkbox-content a {
    color: #1976d2;
    text-decoration: none;
}

.checkbox-content a:hover {
    text-decoration: underline;
}

/* Force the checkbox container to align items at the start */
.align-checkbox-top :deep(.v-input__slot) {
    align-items: flex-start !important;
}
</style>